import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, FileSpreadsheet, ChevronDown, ChevronUp, TrendingUp, Target, BarChart3, CheckCircle, BookOpen, Lock, Upload, Clock, Settings, MousePointer } from 'lucide-react';

export default function CustomModelBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);
  const [isProcessOpen, setIsProcessOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <FileSpreadsheet className="h-5 w-5 sm:h-7 sm:w-7" />
              Custom Model Upload (SGR / FCR)
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            {/* Upload Process Section - Collapsible */}
            <Collapsible open={isProcessOpen} onOpenChange={setIsProcessOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                  data-testid="process-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                    </div>
                    <span className="font-semibold text-purple-700 text-sm sm:text-base">Upload Process & How to Select Your Model</span>
                  </div>
                  {isProcessOpen ? (
                    <ChevronUp className="h-5 w-5 text-purple-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-purple-600" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Step 1: Received */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      1
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Upload className="h-4 w-4 text-blue-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-blue-800">Received</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Once you upload your Excel file containing your SGR or FCR model data, the system immediately marks it as <strong>"Received"</strong>. You'll see this status in your Custom Model Manager dashboard. This confirms your file has been successfully uploaded and is queued for review.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 2: Processing */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      2
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-amber-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-amber-800">Processing</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        The TPS team reviews your model for structural accuracy and validates the data format. During this phase, the status changes to <strong>"Processing"</strong>. This ensures your model meets the technical requirements and will integrate correctly with the analysis tools.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 3: Approved & Installed */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      3
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-green-800">Approved & Installed</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Once verified, your model is marked as <strong>"Approved & Installed"</strong>. It is now active in your account and ready to use. You'll receive confirmation and can immediately begin using your custom model in analyses.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 4: How to Select Your Model */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#0891b2] text-white rounded-full flex items-center justify-center font-bold text-sm">
                      4
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <MousePointer className="h-4 w-4 text-[#0891b2]" />
                        <h4 className="text-sm sm:text-base font-semibold text-[#0891b2]">How to Select Your Custom Model</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        After approval, your custom model becomes available in the following analysis modules:
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <TrendingUp className="h-4 w-4 text-[#0891b2] mt-0.5 flex-shrink-0" />
                          <span><strong>Growth vs Model Analysis:</strong> Select "My Approved Models" from the Model Source dropdown to compare actual growth against your custom SGR model.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <BarChart3 className="h-4 w-4 text-[#0891b2] mt-0.5 flex-shrink-0" />
                          <span><strong>Growth Prognosis:</strong> Choose your custom model to forecast future growth using your validated biological parameters.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-[#0891b2] mt-0.5 flex-shrink-0" />
                          <span><strong>Spreadsheet Growth Comparison:</strong> Apply your custom model when analyzing uploaded spreadsheet data for more accurate comparisons.</span>
                        </li>
                      </ul>
                      <div className="mt-3 p-3 bg-white rounded-lg border border-[#0891b2]/30">
                        <p className="text-xs sm:text-sm text-[#0891b2] font-medium">
                          💡 Tip: Look for the "Model Source" dropdown in each analysis tool. Select "My Approved Models" to see your custom models, or "TPS Standard Model" to use the default.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Custom Model Upload</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 sm:mb-3">
                    Why Upload Your Own Growth or FCR Model?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Every farm operates under unique biological, environmental, and nutritional conditions.
                  </p>
                </section>

                {/* Feature List */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-3">The Custom Model Upload feature allows you to:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Use your own historical SGR curves as the benchmark</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Implement strain-specific or site-specific performance models</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Replace generic industry assumptions with validated farm data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Maintain biological consistency across cycles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Align forecasting with your real operational reality</span>
                    </li>
                  </ul>
                </section>

                {/* Comparison Options */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-3">Approved custom models allow you to compare performance against:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>TPS Standard Models</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Or your own validated biological baseline</span>
                    </li>
                  </ul>
                </section>

                {/* Precision Benefits */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-amber-800 mb-3">This increases precision in:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <TrendingUp className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <span>Growth forecasting</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <Target className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <span>Harvest timing</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <BarChart3 className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <span>Feed budgeting</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <span>Performance benchmarking</span>
                    </div>
                  </div>
                </section>

                {/* Privacy Note */}
                <section className="bg-purple-50 border border-purple-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-full flex-shrink-0">
                      <Lock className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-1">Privacy & Review</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Your models remain <strong>private</strong> and are reviewed by TPS before activation to ensure structural accuracy.
                      </p>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="Custom Model Upload"
              youtubeUrl={HELP_URLS.customModelUpload.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="go-to-dashboard-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
